import {
  fetchGaAdminSubCallFeedbacks,
  fetchGaAdminSubCalls,
  fetchGaAdminTargetCustomers,
} from '@/api/gaAdmin';
import i18n from '@/I18n';
import {getLocalStorage} from '@/utils/index';
import {displayPhysicianName} from '@/filters/index';

const EMPTY_VALUE = '--';
const SOURCE_CACHE_TTL = 5 * 60 * 1000;
const GBA_PROJECT_START_DATE = new Date(2026, 0, 1);
const MAX_FEEDBACK_PROMPT_ITEMS = 40;
const MAX_ALERT_DETAIL_ITEMS = 100;

let sourceCache = {
  key: '',
  expiresAt: 0,
  promise: null,
  data: null,
};

function uniqBy(list, keyGetter) {
  const map = new Map();
  (list || []).forEach((item) => {
    const key = keyGetter(item);
    if (key && !map.has(key)) {
      map.set(key, item);
    }
  });
  return Array.from(map.values());
}

function toId(value) {
  return value === undefined || value === null ? '' : String(value);
}

function percent(numerator, denominator) {
  if (!denominator) return null;
  return Math.round((numerator / denominator) * 100);
}

function formatPercent(value) {
  return value === null || value === undefined ? EMPTY_VALUE : `${value}%`;
}

function isEnglishLocale() {
  return /^en/i.test(i18n.locale || '');
}

function t(key, values) {
  return i18n.t(key, values);
}

function countFeedback(count) {
  return t('ga_admin_count_feedback', {count});
}

function countCustomer(count) {
  return t('ga_admin_count_customer', {count});
}

function countExpert(count) {
  return t('ga_admin_count_expert', {count});
}

function getSourceCacheKey({userId, roleId, tas}) {
  return [userId || '', roleId || '', tas || ''].join('|');
}

async function loadSourceData({userId, roleId, tas}) {
  const key = getSourceCacheKey({userId, roleId, tas});
  const now = Date.now();
  if (sourceCache.key === key && sourceCache.data && sourceCache.expiresAt > now) {
    return sourceCache.data;
  }
  if (sourceCache.key === key && sourceCache.promise) {
    return sourceCache.promise;
  }

  const promise = Promise.all([
    fetchGaAdminTargetCustomers({userId, tas}),
    fetchGaAdminTargetCustomers({userId, tas, hcpCategory: 'expert'}),
    fetchGaAdminSubCalls(),
    fetchGaAdminSubCallFeedbacks(),
  ]).then(([clinicalRaw, expertRaw, calls, feedbacks]) => {
    const data = {clinicalRaw, expertRaw, calls, feedbacks};
    sourceCache = {
      key,
      expiresAt: Date.now() + SOURCE_CACHE_TTL,
      promise: null,
      data,
    };
    return data;
  }).catch((error) => {
    if (sourceCache.key === key) {
      sourceCache = {
        key: '',
        expiresAt: 0,
        promise: null,
        data: null,
      };
    }
    throw error;
  });

  sourceCache = {
    key,
    expiresAt: now + SOURCE_CACHE_TTL,
    promise,
    data: null,
  };
  return promise;
}

function isExpertCustomer(item) {
  const category = String(item?.hcp_category || item?.physician_category || item?.customer_type || '').toLowerCase();
  return category.includes('expert') || category.includes('he') || item?.is_expert === 1;
}

function getCallId(call) {
  return toId(call?.physician_mr_call_id || call?.id);
}

function getCallPhysicianId(call) {
  return toId(call?.physician_id || call?.physician?.physician_id);
}

function isVisitedCall(call) {
  if (!call?.call_time) return true;
  const callTime = new Date(call.call_time).getTime();
  if (Number.isNaN(callTime)) return true;
  return callTime <= Date.now();
}

function getMonthKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

function getMonthLabel(monthKey) {
  const [, month] = monthKey.split('-');
  if (!isEnglishLocale()) return t('ga_admin_month_label', {month: Number(month)});
  return new Date(2026, Number(month) - 1, 1).toLocaleString('en-US', {month: 'short'});
}

function addMonths(date, offset) {
  const result = new Date(date.getFullYear(), date.getMonth() + offset, 1);
  result.setHours(0, 0, 0, 0);
  return result;
}

function getMonthStart(date) {
  const result = new Date(date.getFullYear(), date.getMonth(), 1);
  result.setHours(0, 0, 0, 0);
  return result;
}

function getMonthKeysFromProjectStart(endDate = new Date()) {
  const start = getMonthStart(GBA_PROJECT_START_DATE);
  const currentMonth = getMonthStart(new Date());
  const requestedEnd = getMonthStart(endDate);
  const end = requestedEnd > currentMonth ? requestedEnd : currentMonth;
  const monthKeys = [];
  for (let cursor = start; cursor <= end; cursor = addMonths(cursor, 1)) {
    monthKeys.push(getMonthKey(cursor));
  }
  return monthKeys;
}

function createMonthlyCounts(monthKeys) {
  return monthKeys.reduce((result, monthKey) => {
    result[monthKey] = {month: monthKey, label: getMonthLabel(monthKey), gba: 0, ga: 0, total: 0};
    return result;
  }, {});
}

function classifyCalls(calls, clinicalIds, expertIds) {
  const result = {ga: [], gba: []};
  (calls || []).forEach((call) => {
    const physicianId = getCallPhysicianId(call);
    let team = '';

    if (expertIds.has(physicianId)) team = 'ga';
    if (!team && clinicalIds.has(physicianId)) team = 'gba';

    if (team) {
      result[team].push(call);
    }
  });

  return result;
}

function getFeedbackQuestionText(feedback) {
  const question = feedback?.key_question || {};
  return question.question || question.key_question_name || question.name || feedback?.question || '';
}

function getFeedbackText(feedback) {
  return String(
    feedback?.feedback_content ||
    feedback?.feedback_summary ||
    feedback?.feedback_transcript ||
    '',
  ).trim();
}

function truncateText(text, maxLength = 90) {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength)}...` : normalized;
}

function formatDateText(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function getCustomerName(item) {
  const name = item?.physician_name || item?.hcp_name || item?.name || item?.physician?.physician_name;
  return displayPhysicianName(name) || t('ga_admin_unknown_customer');
}

function getCallUserName(call) {
  return call?.call_user_name || call?.user_name || call?.owner_name || call?.user?.user_name || '';
}

function getCustomerMeta(item) {
  return [
    item?.institution_name || item?.physician?.institution_name,
    item?.department_name || item?.physician?.department_name,
    item?.professional_title || item?.academic_title || item?.physician?.professional_title,
  ].filter(Boolean).join('｜');
}

function getCallTitle(call, fallbackCustomer) {
  return getCustomerName(call) !== t('ga_admin_unknown_customer') ? getCustomerName(call) : getCustomerName(fallbackCustomer);
}

function getCallMeta(call, fallbackCustomer) {
  const callDate = formatDateText(call?.call_time);
  return [
    callDate ? t('ga_admin_call_date_meta', {date: callDate}) : '',
    getCallUserName(call) ? t('ga_admin_call_owner_meta', {owner: getCallUserName(call)}) : '',
    getCustomerMeta(call) || getCustomerMeta(fallbackCustomer),
  ].filter(Boolean).join('｜');
}

function getChoiceScore(feedback) {
  const text = getFeedbackText(feedback);
  if (/^[0-3]$/.test(text)) return Number(text);
  const match = text.match(/[0-3]/);
  return match ? Number(match[0]) : null;
}

function includesAny(text, words) {
  return words.some(word => text.indexOf(word) > -1);
}

function isClinicalPositive(feedback) {
  const answer = getFeedbackText(feedback);
  if (includesAny(answer, ['不认同', '不必要'])) return false;
  return includesAny(answer, ['完全认同', '基本认同', '非常必要']) || answer === '必要';
}

function analyzeFeedbacks(feedbacks, callTeamById) {
  const result = {
    clinical: {
      diseasePositive: 0,
      diseaseTotal: 0,
      burdenPositive: 0,
      burdenTotal: 0,
      openTextCount: 0,
      recognizedCount: 0,
    },
    expert: {
      familiarityTotal: 0,
      familiarityScore: 0,
      communicationPositive: 0,
      communicationTotal: 0,
      openTextCount: 0,
      recognizedCount: 0,
    },
    labels: {
      clinical: [],
      expert: [],
    },
    unrecognizedCount: 0,
  };

  (feedbacks || []).forEach((feedback) => {
    const team = callTeamById[getFeedbackCallId(feedback)];
    const questionText = getFeedbackQuestionText(feedback);
    const answer = getFeedbackText(feedback);
    const labels = String(feedback?.feedback_labels || '').split(',').map(item => item.trim()).filter(Boolean);

    let recognized = false;
    if (team === 'gba') {
      result.labels.clinical.push(...labels);
      if (includesAny(questionText, ['疾病认知']) && !includesAny(questionText, ['慢性'])) {
        result.clinical.diseaseTotal += 1;
        if (isClinicalPositive(feedback)) result.clinical.diseasePositive += 1;
        recognized = true;
      } else if (includesAny(questionText, ['疾病负担', '慢性偏头痛'])) {
        result.clinical.burdenTotal += 1;
        if (isClinicalPositive(feedback)) result.clinical.burdenPositive += 1;
        recognized = true;
      } else if (includesAny(questionText, ['治疗现状', '创新需求']) || answer.length > 8) {
        result.clinical.openTextCount += answer ? 1 : 0;
        recognized = true;
      }
      if (recognized) result.clinical.recognizedCount += 1;
    }

    if (team === 'ga') {
      result.labels.expert.push(...labels);
      if (includesAny(questionText, ['熟悉程度', '熟悉'])) {
        const score = getChoiceScore(feedback);
        if (score !== null) {
          result.expert.familiarityTotal += 1;
          result.expert.familiarityScore += score;
        }
        recognized = true;
      } else if (includesAny(questionText, ['沟通信息认同程度', '沟通认同', '认同程度'])) {
        const score = getChoiceScore(feedback);
        if (score !== null) {
          result.expert.communicationTotal += 1;
          if (score >= 2) result.expert.communicationPositive += 1;
        }
        recognized = true;
      } else if (includesAny(questionText, ['拜访反馈']) || answer.length > 8) {
        result.expert.openTextCount += answer ? 1 : 0;
        recognized = true;
      }
      if (recognized) result.expert.recognizedCount += 1;
    }

    if (!recognized) {
      result.unrecognizedCount += 1;
    }
  });

  return result;
}

function isRecognizedFeedback(feedback, team) {
  const questionText = getFeedbackQuestionText(feedback);
  const answer = getFeedbackText(feedback);

  if (team === 'gba') {
    return (
      (includesAny(questionText, ['疾病认知']) && !includesAny(questionText, ['慢性'])) ||
      includesAny(questionText, ['疾病负担', '慢性偏头痛']) ||
      includesAny(questionText, ['治疗现状', '创新需求']) ||
      answer.length > 8
    );
  }

  if (team === 'ga') {
    return (
      includesAny(questionText, ['熟悉程度', '熟悉']) ||
      includesAny(questionText, ['沟通信息认同程度', '沟通认同', '认同程度']) ||
      includesAny(questionText, ['拜访反馈']) ||
      answer.length > 8
    );
  }

  return false;
}

function getFeedbackCallId(feedback) {
  return toId(feedback?.physician_mr_call_id || feedback?.source_msl_call_id);
}

function getFeedbackPhysicianId(feedback, callById) {
  return toId(feedback?.physician_id || callById[getFeedbackCallId(feedback)]?.physician_id);
}

function isPositiveFeedback(feedback, team) {
  const questionText = getFeedbackQuestionText(feedback);
  if (team === 'gba') {
    if (
      (includesAny(questionText, ['疾病认知']) && !includesAny(questionText, ['慢性'])) ||
      includesAny(questionText, ['疾病负担', '慢性偏头痛'])
    ) {
      return isClinicalPositive(feedback);
    }
    return false;
  }
  if (team === 'ga' && includesAny(questionText, ['沟通信息认同程度', '沟通认同', '认同程度'])) {
    const score = getChoiceScore(feedback);
    return score !== null && score >= 2;
  }
  return false;
}

function buildTeamStats(customers, calls, feedbacks) {
  const targetIds = new Set(customers.map(item => toId(item.physician_id)).filter(Boolean));
  const visitedCalls = calls.filter(isVisitedCall);
  const coveredIds = new Set(visitedCalls.map(getCallPhysicianId).filter(id => targetIds.has(id)));
  const callIds = new Set(calls.map(getCallId).filter(Boolean));
  const callsWithFeedback = new Set((feedbacks || []).map(getFeedbackCallId).filter(id => callIds.has(id)));

  return {
    targetCount: customers.length,
    callCount: calls.length,
    visitedCount: visitedCalls.length,
    plannedCount: calls.length - visitedCalls.length,
    coveredCount: coveredIds.size,
    coverageRate: percent(coveredIds.size, customers.length),
    feedbackCallCount: callsWithFeedback.size,
    feedbackRate: percent(callsWithFeedback.size, calls.length),
  };
}

function buildFeedbackCustomerStats(feedbacks, team, callById) {
  const feedbackCustomerIds = new Set();
  const positiveCustomerIds = new Set();

  (feedbacks || []).forEach((feedback) => {
    const physicianId = getFeedbackPhysicianId(feedback, callById);
    if (!physicianId) return;
    feedbackCustomerIds.add(physicianId);
    if (isPositiveFeedback(feedback, team)) {
      positiveCustomerIds.add(physicianId);
    }
  });

  return {
    feedbackCustomerCount: feedbackCustomerIds.size,
    positiveFeedbackCustomerCount: positiveCustomerIds.size,
  };
}

function getTopLabels(labels) {
  const counts = {};
  (labels || []).forEach((label) => {
    counts[label] = (counts[label] || 0) + 1;
  });
  const sorted = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  return sorted.slice(0, 4);
}

function buildOverviewStats(teamStats, feedbackAnalysis) {
  const clinicalAlignment = percent(
    feedbackAnalysis.clinical.diseasePositive + feedbackAnalysis.clinical.burdenPositive,
    feedbackAnalysis.clinical.diseaseTotal + feedbackAnalysis.clinical.burdenTotal,
  );
  const expertAlignment = percent(
    feedbackAnalysis.expert.communicationPositive,
    feedbackAnalysis.expert.communicationTotal,
  );

  return [
    {
      title: t('ga_admin_analytics_stat_clinical_alignment_title'),
      value: formatPercent(clinicalAlignment),
      change: t('ga_admin_analytics_stat_choice_feedback_change', {
        count: feedbackAnalysis.clinical.diseaseTotal + feedbackAnalysis.clinical.burdenTotal,
      }),
      tone: clinicalAlignment === null || clinicalAlignment >= 70 ? 'positive' : 'warning',
      desc: t('ga_admin_analytics_stat_clinical_alignment_desc'),
    },
    {
      title: t('ga_admin_analytics_stat_expert_alignment_title'),
      value: formatPercent(expertAlignment),
      change: t('ga_admin_analytics_stat_expert_feedback_change', {
        count: feedbackAnalysis.expert.communicationTotal,
      }),
      tone: expertAlignment === null || expertAlignment >= 70 ? 'positive' : 'warning',
      desc: t('ga_admin_analytics_stat_expert_alignment_desc'),
    },
    {
      title: t('ga_admin_analytics_stat_gba_coverage_title'),
      value: formatPercent(teamStats.gba.coverageRate),
      change: t('ga_admin_analytics_stat_coverage_change', {
        covered: teamStats.gba.coveredCount,
        total: teamStats.gba.targetCount,
      }),
      tone: teamStats.gba.coverageRate === null || teamStats.gba.coverageRate >= 70 ? 'neutral' : 'warning',
      desc: t('ga_admin_analytics_stat_gba_coverage_desc', {count: teamStats.gba.visitedCount}),
    },
    {
      title: t('ga_admin_analytics_stat_ga_coverage_title'),
      value: formatPercent(teamStats.ga.coverageRate),
      change: t('ga_admin_analytics_stat_coverage_change', {
        covered: teamStats.ga.coveredCount,
        total: teamStats.ga.targetCount,
      }),
      tone: teamStats.ga.coverageRate === null || teamStats.ga.coverageRate >= 70 ? 'neutral' : 'warning',
      desc: t('ga_admin_analytics_stat_ga_coverage_desc', {count: teamStats.ga.visitedCount}),
    },
  ];
}

function buildHighlights(teamStats, feedbackAnalysis) {
  const clinicalAlignment = percent(
    feedbackAnalysis.clinical.diseasePositive + feedbackAnalysis.clinical.burdenPositive,
    feedbackAnalysis.clinical.diseaseTotal + feedbackAnalysis.clinical.burdenTotal,
  );
  const expertAlignment = percent(
    feedbackAnalysis.expert.communicationPositive,
    feedbackAnalysis.expert.communicationTotal,
  );
  const totalGap = Math.max(teamStats.gba.targetCount - teamStats.gba.coveredCount, 0) +
    Math.max(teamStats.ga.targetCount - teamStats.ga.coveredCount, 0);

  return [
    t('ga_admin_analytics_highlight_coverage', {
      gbaRate: formatPercent(teamStats.gba.coverageRate),
      gaRate: formatPercent(teamStats.ga.coverageRate),
    }),
    t('ga_admin_analytics_highlight_alignment', {
      clinicalRate: formatPercent(clinicalAlignment),
      expertRate: formatPercent(expertAlignment),
    }),
    t('ga_admin_analytics_highlight_feedback_rate', {
      gbaRate: formatPercent(teamStats.gba.feedbackRate),
      gaRate: formatPercent(teamStats.ga.feedbackRate),
    }),
    totalGap
      ? t('ga_admin_analytics_highlight_gap', {count: totalGap})
      : t('ga_admin_analytics_highlight_no_gap'),
  ];
}

function buildMindsetBoards(feedbackAnalysis) {
  const clinicalDisease = percent(feedbackAnalysis.clinical.diseasePositive, feedbackAnalysis.clinical.diseaseTotal);
  const clinicalBurden = percent(feedbackAnalysis.clinical.burdenPositive, feedbackAnalysis.clinical.burdenTotal);
  const familiarity = feedbackAnalysis.expert.familiarityTotal
    ? Math.round((feedbackAnalysis.expert.familiarityScore / feedbackAnalysis.expert.familiarityTotal / 3) * 100)
    : null;
  const expertAlignment = percent(feedbackAnalysis.expert.communicationPositive, feedbackAnalysis.expert.communicationTotal);

  return [
    {
      title: t('ga_admin_mock_board_clinical_title'),
      subtitle: t('ga_admin_team_gba'),
      summary: t('ga_admin_analytics_board_clinical_summary', {
        recognizedCount: feedbackAnalysis.clinical.recognizedCount,
        openTextCount: feedbackAnalysis.clinical.openTextCount,
      }),
      metrics: [
        { label: t('ga_admin_analytics_metric_disease_alignment'), value: formatPercent(clinicalDisease) },
        { label: t('ga_admin_analytics_metric_burden_alignment'), value: formatPercent(clinicalBurden) },
        { label: t('ga_admin_analytics_metric_text_feedback'), value: countFeedback(feedbackAnalysis.clinical.openTextCount) },
      ],
      tags: getTopLabels(feedbackAnalysis.labels.clinical),
    },
    {
      title: t('ga_admin_mock_board_expert_title'),
      subtitle: t('ga_admin_team_ga'),
      summary: t('ga_admin_analytics_board_expert_summary', {
        recognizedCount: feedbackAnalysis.expert.recognizedCount,
      }),
      metrics: [
        { label: t('ga_admin_analytics_metric_customer_familiarity'), value: formatPercent(familiarity) },
        { label: t('ga_admin_analytics_metric_communication_alignment'), value: formatPercent(expertAlignment) },
        { label: t('ga_admin_analytics_metric_text_feedback'), value: countFeedback(feedbackAnalysis.expert.openTextCount) },
      ],
      tags: getTopLabels(feedbackAnalysis.labels.expert),
    },
  ];
}

function buildCustomerPanoramas(teamStats, feedbackAnalysis) {
  const clinicalDisease = percent(feedbackAnalysis.clinical.diseasePositive, feedbackAnalysis.clinical.diseaseTotal);
  const clinicalBurden = percent(feedbackAnalysis.clinical.burdenPositive, feedbackAnalysis.clinical.burdenTotal);
  const clinicalAlignment = percent(
    feedbackAnalysis.clinical.diseasePositive + feedbackAnalysis.clinical.burdenPositive,
    feedbackAnalysis.clinical.diseaseTotal + feedbackAnalysis.clinical.burdenTotal,
  );
  const expertFamiliarity = feedbackAnalysis.expert.familiarityTotal
    ? Math.round((feedbackAnalysis.expert.familiarityScore / feedbackAnalysis.expert.familiarityTotal / 3) * 100)
    : null;
  const expertAlignment = percent(feedbackAnalysis.expert.communicationPositive, feedbackAnalysis.expert.communicationTotal);
  const clinicalGap = Math.max(teamStats.gba.targetCount - teamStats.gba.coveredCount, 0);
  const expertGap = Math.max(teamStats.ga.targetCount - teamStats.ga.coveredCount, 0);

  return {
    clinical: {
      summary: t('ga_admin_analytics_customer_clinical_summary', {
        targetCount: teamStats.gba.targetCount,
        coveredCount: teamStats.gba.coveredCount,
        coverageRate: formatPercent(teamStats.gba.coverageRate),
        feedbackCustomerCount: teamStats.gba.feedbackCustomerCount,
        positiveFeedbackCustomerCount: teamStats.gba.positiveFeedbackCustomerCount,
        clinicalAlignment: formatPercent(clinicalAlignment),
        clinicalDisease: formatPercent(clinicalDisease),
        clinicalBurden: formatPercent(clinicalBurden),
        gapText: clinicalGap
          ? t('ga_admin_analytics_customer_clinical_gap', {count: clinicalGap})
          : t('ga_admin_analytics_customer_clinical_no_gap'),
      }),
      prompt: t('ga_admin_analytics_customer_clinical_prompt', {
        targetCount: teamStats.gba.targetCount,
        coveredCount: teamStats.gba.coveredCount,
        coverageRate: formatPercent(teamStats.gba.coverageRate),
        feedbackCustomerCount: teamStats.gba.feedbackCustomerCount,
        positiveFeedbackCustomerCount: teamStats.gba.positiveFeedbackCustomerCount,
        clinicalAlignment: formatPercent(clinicalAlignment),
      }),
      metrics: [
        { label: t('ga_admin_analytics_metric_total_customers'), value: `${teamStats.gba.targetCount}` },
        { label: t('ga_admin_analytics_metric_coverage_rate'), value: formatPercent(teamStats.gba.coverageRate) },
        { label: t('ga_admin_analytics_metric_feedback_customers'), value: `${teamStats.gba.feedbackCustomerCount}` },
        { label: t('ga_admin_analytics_metric_positive_feedback_customers'), value: `${teamStats.gba.positiveFeedbackCustomerCount}` },
        { label: t('ga_admin_analytics_metric_clinical_alignment'), value: formatPercent(clinicalAlignment) },
      ],
    },
    expert: {
      summary: t('ga_admin_analytics_customer_expert_summary', {
        targetCount: teamStats.ga.targetCount,
        coveredCount: teamStats.ga.coveredCount,
        coverageRate: formatPercent(teamStats.ga.coverageRate),
        feedbackCustomerCount: teamStats.ga.feedbackCustomerCount,
        positiveFeedbackCustomerCount: teamStats.ga.positiveFeedbackCustomerCount,
        expertFamiliarity: formatPercent(expertFamiliarity),
        expertAlignment: formatPercent(expertAlignment),
        gapText: expertGap
          ? t('ga_admin_analytics_customer_expert_gap', {count: expertGap})
          : t('ga_admin_analytics_customer_expert_no_gap'),
      }),
      prompt: t('ga_admin_analytics_customer_expert_prompt', {
        targetCount: teamStats.ga.targetCount,
        coveredCount: teamStats.ga.coveredCount,
        coverageRate: formatPercent(teamStats.ga.coverageRate),
        feedbackCustomerCount: teamStats.ga.feedbackCustomerCount,
        positiveFeedbackCustomerCount: teamStats.ga.positiveFeedbackCustomerCount,
        expertFamiliarity: formatPercent(expertFamiliarity),
        expertAlignment: formatPercent(expertAlignment),
      }),
      metrics: [
        { label: t('ga_admin_analytics_metric_total_customers'), value: `${teamStats.ga.targetCount}` },
        { label: t('ga_admin_analytics_metric_coverage_rate'), value: formatPercent(teamStats.ga.coverageRate) },
        { label: t('ga_admin_analytics_metric_feedback_customers'), value: `${teamStats.ga.feedbackCustomerCount}` },
        { label: t('ga_admin_analytics_metric_positive_feedback_customers'), value: `${teamStats.ga.positiveFeedbackCustomerCount}` },
        { label: t('ga_admin_analytics_metric_communication_alignment'), value: formatPercent(expertAlignment) },
      ],
    },
  };
}

function buildExecutionCards(teamStats) {
  const totalCalls = teamStats.gba.visitedCount + teamStats.ga.visitedCount;
  const totalFeedbackCalls = teamStats.gba.feedbackCallCount + teamStats.ga.feedbackCallCount;
  const totalCallCount = teamStats.gba.callCount + teamStats.ga.callCount;
  const feedbackRate = percent(totalFeedbackCalls, totalCallCount);

  return [
    {
      title: t('ga_admin_analytics_stat_gba_coverage_title'),
      value: formatPercent(teamStats.gba.coverageRate),
      subValue: t('ga_admin_analytics_execution_gba_coverage_subvalue', {
        covered: teamStats.gba.coveredCount,
        total: teamStats.gba.targetCount,
      }),
      tone: teamStats.gba.coverageRate === null || teamStats.gba.coverageRate >= 70 ? 'positive' : 'warning',
    },
    {
      title: t('ga_admin_analytics_stat_ga_coverage_title'),
      value: formatPercent(teamStats.ga.coverageRate),
      subValue: t('ga_admin_analytics_execution_ga_coverage_subvalue', {
        covered: teamStats.ga.coveredCount,
        total: teamStats.ga.targetCount,
      }),
      tone: teamStats.ga.coverageRate === null || teamStats.ga.coverageRate >= 70 ? 'positive' : 'warning',
    },
    {
      title: t('ga_admin_analytics_execution_visits_title'),
      value: String(totalCalls),
      subValue: t('ga_admin_analytics_execution_visits_subvalue'),
      tone: 'neutral',
    },
    {
      title: t('ga_admin_analytics_execution_feedback_rate_title'),
      value: formatPercent(feedbackRate),
      subValue: t('ga_admin_analytics_execution_feedback_rate_subvalue', {
        completed: totalFeedbackCalls,
        total: totalCallCount,
      }),
      tone: feedbackRate === null || feedbackRate >= 70 ? 'positive' : 'warning',
    },
  ];
}

function buildExecutionPipelines(teamStats) {
  return {
    gba: [
      { stage: t('ga_admin_analytics_pipeline_total_customers'), count: teamStats.gba.targetCount },
      { stage: t('ga_admin_analytics_pipeline_covered_customers'), count: teamStats.gba.coveredCount },
      { stage: t('ga_admin_analytics_pipeline_feedback_customers'), count: teamStats.gba.feedbackCustomerCount },
      { stage: t('ga_admin_analytics_pipeline_positive_feedback_customers'), count: teamStats.gba.positiveFeedbackCustomerCount },
    ],
    ga: [
      { stage: t('ga_admin_analytics_pipeline_total_customers'), count: teamStats.ga.targetCount },
      { stage: t('ga_admin_analytics_pipeline_covered_customers'), count: teamStats.ga.coveredCount },
      { stage: t('ga_admin_analytics_pipeline_feedback_customers'), count: teamStats.ga.feedbackCustomerCount },
      { stage: t('ga_admin_analytics_pipeline_positive_feedback_customers'), count: teamStats.ga.positiveFeedbackCustomerCount },
    ],
  };
}

function buildPositiveFeedbackRules() {
  return [
    t('ga_admin_analytics_positive_rule_gba'),
    t('ga_admin_analytics_positive_rule_ga'),
  ];
}

function getLatestDateFromItems(items, dateGetter) {
  const latestTime = (items || [])
    .map(item => new Date(dateGetter(item)).getTime())
    .filter(time => !Number.isNaN(time))
    .sort((a, b) => b - a)[0];
  return latestTime ? new Date(latestTime) : new Date();
}

function buildMonthlyVisitChart(classifiedCalls, monthKeys) {
  const visitedCalls = [
    ...classifiedCalls.gba.map(call => ({...call, team: 'gba'})),
    ...classifiedCalls.ga.map(call => ({...call, team: 'ga'})),
  ].filter(isVisitedCall);
  const counts = createMonthlyCounts(monthKeys);

  visitedCalls.forEach((call) => {
    if (!call.call_time) return;
    const callDate = new Date(call.call_time);
    if (Number.isNaN(callDate.getTime())) return;
    const monthKey = getMonthKey(callDate);
    if (!counts[monthKey]) return;
    counts[monthKey][call.team] += 1;
    counts[monthKey].total += 1;
  });

  return monthKeys.map(monthKey => counts[monthKey]);
}

function buildMonthlyFeedbackChart(feedbacks, callTeamById, callById, monthKeys) {
  const counts = createMonthlyCounts(monthKeys);

  (feedbacks || []).forEach((feedback) => {
    const callId = getFeedbackCallId(feedback);
    const team = callTeamById[callId];
    if (!team) return;
    const feedbackDate = new Date(feedback.feedback_time || callById[callId]?.call_time);
    if (Number.isNaN(feedbackDate.getTime())) return;
    const monthKey = getMonthKey(feedbackDate);
    if (!counts[monthKey]) return;
    counts[monthKey][team] += 1;
    counts[monthKey].total += 1;
  });

  return monthKeys.map(monthKey => counts[monthKey]);
}

function buildCollaborationQueue(teamStats, feedbackAnalysis) {
  const clinicalGap = Math.max(teamStats.gba.targetCount - teamStats.gba.coveredCount, 0);
  const expertGap = Math.max(teamStats.ga.targetCount - teamStats.ga.coveredCount, 0);
  const missingFeedback = Math.max(
    teamStats.gba.callCount + teamStats.ga.callCount - teamStats.gba.feedbackCallCount - teamStats.ga.feedbackCallCount,
    0,
  );
  const queue = [];

  if (clinicalGap) {
    queue.push({
      title: t('ga_admin_analytics_queue_clinical_title'),
      status: t('ga_admin_analytics_status_pending_follow_up'),
      owners: t('ga_admin_team_gba'),
      desc: t('ga_admin_analytics_queue_clinical_desc', {count: clinicalGap}),
    });
  }
  if (expertGap) {
    queue.push({
      title: t('ga_admin_analytics_queue_expert_title'),
      status: t('ga_admin_analytics_status_pending_follow_up'),
      owners: t('ga_admin_team_ga'),
      desc: t('ga_admin_analytics_queue_expert_desc', {count: expertGap}),
    });
  }
  if (missingFeedback || feedbackAnalysis.unrecognizedCount) {
    queue.push({
      title: t('ga_admin_analytics_queue_feedback_title'),
      status: t('ga_admin_analytics_status_in_progress'),
      owners: t('ga_admin_analytics_owner_ga_gba'),
      desc: t('ga_admin_analytics_queue_feedback_desc', {
        missingFeedback,
        unrecognizedCount: feedbackAnalysis.unrecognizedCount,
      }),
    });
  }

  return queue.length ? queue : [
    {
      title: t('ga_admin_analytics_queue_monitor_title'),
      status: t('ga_admin_analytics_status_normal'),
      owners: t('ga_admin_analytics_owner_ga_gba'),
      desc: t('ga_admin_analytics_queue_monitor_desc'),
    },
  ];
}

function buildAlertDetailItem({primary, secondary, meta}) {
  return {
    primary: primary || t('ga_admin_unknown'),
    secondary: secondary || '',
    meta: meta || '',
  };
}

function limitAlertDetails(details) {
  const list = details.slice(0, MAX_ALERT_DETAIL_ITEMS);
  return {
    list,
    hiddenCount: Math.max(details.length - list.length, 0),
  };
}

function buildQualityAlertsWithDetails({
  teamStats,
  feedbackAnalysis,
  clinicalCustomers = [],
  expertCustomers = [],
  classifiedCalls = {gba: [], ga: []},
  feedbacks = [],
  callById = {},
  callTeamById = {},
}) {
  const alerts = [];
  const clinicalCustomerById = {};
  const expertCustomerById = {};
  clinicalCustomers.forEach((item) => { clinicalCustomerById[toId(item.physician_id)] = item; });
  expertCustomers.forEach((item) => { expertCustomerById[toId(item.physician_id)] = item; });

  const buildUncoveredCustomerAlert = ({teamKey, customers, calls, title, message, kind}) => {
    const coveredIds = new Set(calls.filter(isVisitedCall).map(getCallPhysicianId).filter(Boolean));
    const details = customers
      .filter(customer => !coveredIds.has(toId(customer.physician_id)))
      .map(customer => buildAlertDetailItem({
        primary: getCustomerName(customer),
        secondary: getCustomerMeta(customer),
        meta: '',
      }));
    if (!details.length) return;
    const limited = limitAlertDetails(details);
    alerts.push({
      id: `${teamKey}-uncovered`,
      message,
      detailTitle: title,
      detailType: kind,
      details: limited.list,
      totalCount: details.length,
      hiddenCount: limited.hiddenCount,
    });
  };

  buildUncoveredCustomerAlert({
    teamKey: 'gba',
    customers: clinicalCustomers,
    calls: classifiedCalls.gba,
    title: t('ga_admin_analytics_alert_uncovered_clinical_title'),
    message: t('ga_admin_analytics_alert_uncovered_clinical_message', {
      count: Math.max(teamStats.gba.targetCount - teamStats.gba.coveredCount, 0),
    }),
    kind: t('ga_admin_alert_customer'),
  });
  buildUncoveredCustomerAlert({
    teamKey: 'ga',
    customers: expertCustomers,
    calls: classifiedCalls.ga,
    title: t('ga_admin_analytics_alert_uncovered_expert_title'),
    message: t('ga_admin_analytics_alert_uncovered_expert_message', {
      count: Math.max(teamStats.ga.targetCount - teamStats.ga.coveredCount, 0),
    }),
    kind: t('ga_admin_alert_expert'),
  });

  const feedbackCallIds = new Set(feedbacks.map(getFeedbackCallId).filter(Boolean));
  const allTeamCalls = [
    ...classifiedCalls.gba.map(call => ({call, team: 'GBA', customerMap: clinicalCustomerById})),
    ...classifiedCalls.ga.map(call => ({call, team: 'GA', customerMap: expertCustomerById})),
  ];
  const missingFeedbackDetails = allTeamCalls
    .filter(({call}) => isVisitedCall(call) && !feedbackCallIds.has(getCallId(call)))
    .map(({call, team, customerMap}) => {
      const customer = customerMap[getCallPhysicianId(call)];
      return buildAlertDetailItem({
        primary: getCallTitle(call, customer),
        secondary: getCallMeta(call, customer),
        meta: team,
      });
    });

  if (missingFeedbackDetails.length) {
    const limited = limitAlertDetails(missingFeedbackDetails);
    alerts.push({
      id: 'missing-feedback',
      message: t('ga_admin_analytics_alert_missing_feedback_message', {count: missingFeedbackDetails.length}),
      detailTitle: t('ga_admin_analytics_alert_missing_feedback_title'),
      detailType: t('ga_admin_alert_visit'),
      details: limited.list,
      totalCount: missingFeedbackDetails.length,
      hiddenCount: limited.hiddenCount,
    });
  }

  const unrecognizedDetails = feedbacks
    .filter((feedback) => {
      const team = callTeamById[getFeedbackCallId(feedback)];
      return !isRecognizedFeedback(feedback, team);
    })
    .map((feedback) => {
      const call = callById[getFeedbackCallId(feedback)];
      const team = callTeamById[getFeedbackCallId(feedback)] === 'ga' ? 'GA' : 'GBA';
      const customerMap = team === 'GA' ? expertCustomerById : clinicalCustomerById;
      const customer = customerMap[getFeedbackPhysicianId(feedback, callById)] || call;
      return buildAlertDetailItem({
        primary: getCallTitle(call, customer),
        secondary: [
          getCallUserName(call) ? t('ga_admin_call_owner_meta', {owner: getCallUserName(call)}) : '',
          getFeedbackQuestionText(feedback) || t('ga_admin_unnamed_key_question'),
          truncateText(getFeedbackText(feedback), 70),
        ].filter(Boolean).join('｜'),
        meta: [
          team,
          formatDateText(feedback.feedback_time || call?.call_time),
        ].filter(Boolean).join('｜'),
      });
    });

  if (unrecognizedDetails.length || feedbackAnalysis.unrecognizedCount) {
    const limited = limitAlertDetails(unrecognizedDetails);
    alerts.push({
      id: 'unrecognized-feedback',
      message: t('ga_admin_analytics_alert_unrecognized_message', {
        count: unrecognizedDetails.length || feedbackAnalysis.unrecognizedCount,
      }),
      detailTitle: t('ga_admin_analytics_alert_unrecognized_title'),
      detailType: t('ga_admin_alert_feedback'),
      details: limited.list,
      totalCount: unrecognizedDetails.length || feedbackAnalysis.unrecognizedCount,
      hiddenCount: limited.hiddenCount,
    });
  }

  if (!alerts.length) {
    alerts.push({
      id: 'healthy',
      message: t('ga_admin_analytics_alert_healthy'),
      detailTitle: '',
      detailType: '',
      details: [],
      totalCount: 0,
      hiddenCount: 0,
    });
  }

  return alerts;
}

function buildStrategyRecommendations(teamStats, feedbackAnalysis) {
  const clinicalGap = Math.max(teamStats.gba.targetCount - teamStats.gba.coveredCount, 0);
  const expertGap = Math.max(teamStats.ga.targetCount - teamStats.ga.coveredCount, 0);
  const missingFeedback = Math.max(
    teamStats.gba.callCount + teamStats.ga.callCount - teamStats.gba.feedbackCallCount - teamStats.ga.feedbackCallCount,
    0,
  );
  const clinicalAlignment = percent(
    feedbackAnalysis.clinical.diseasePositive + feedbackAnalysis.clinical.burdenPositive,
    feedbackAnalysis.clinical.diseaseTotal + feedbackAnalysis.clinical.burdenTotal,
  );
  const expertAlignment = percent(
    feedbackAnalysis.expert.communicationPositive,
    feedbackAnalysis.expert.communicationTotal,
  );

  return [
    {
      title: t('ga_admin_analytics_strategy_coverage_title'),
      level: expertGap > 0 || clinicalGap > 0 ? t('ga_admin_priority_high') : t('ga_admin_priority_medium'),
      desc: t('ga_admin_analytics_strategy_coverage_desc', {clinicalGap, expertGap}),
      actions: [
        clinicalGap
          ? t('ga_admin_analytics_strategy_coverage_action_clinical_gap', {count: clinicalGap})
          : t('ga_admin_analytics_strategy_coverage_action_clinical_keep'),
        expertGap
          ? t('ga_admin_analytics_strategy_coverage_action_expert_gap', {count: expertGap})
          : t('ga_admin_analytics_strategy_coverage_action_expert_keep'),
        t('ga_admin_analytics_strategy_coverage_action_sequence'),
      ],
      prompt: t('ga_admin_analytics_strategy_coverage_prompt', {
        clinicalGap,
        expertGap,
        gbaRate: formatPercent(teamStats.gba.feedbackRate),
        gaRate: formatPercent(teamStats.ga.feedbackRate),
      }),
    },
    {
      title: t('ga_admin_analytics_strategy_feedback_title'),
      level: teamStats.gba.feedbackRate < 70 || teamStats.ga.feedbackRate < 70 ? t('ga_admin_priority_high') : t('ga_admin_priority_medium'),
      desc: t('ga_admin_analytics_strategy_feedback_desc', {
        gbaRate: formatPercent(teamStats.gba.feedbackRate),
        gaRate: formatPercent(teamStats.ga.feedbackRate),
        missingFeedback,
      }),
      actions: [
        missingFeedback
          ? t('ga_admin_analytics_strategy_feedback_action_missing', {count: missingFeedback})
          : t('ga_admin_analytics_strategy_feedback_action_keep'),
        feedbackAnalysis.unrecognizedCount
          ? t('ga_admin_analytics_strategy_feedback_action_unrecognized', {count: feedbackAnalysis.unrecognizedCount})
          : t('ga_admin_analytics_strategy_feedback_action_match_keep'),
        t('ga_admin_analytics_strategy_feedback_action_labels'),
      ],
      prompt: t('ga_admin_analytics_strategy_feedback_prompt', {
        missingFeedback,
        unrecognizedCount: feedbackAnalysis.unrecognizedCount,
        gbaRate: formatPercent(teamStats.gba.feedbackRate),
        gaRate: formatPercent(teamStats.ga.feedbackRate),
      }),
    },
    {
      title: t('ga_admin_analytics_strategy_alignment_title'),
      level: clinicalAlignment < 70 || expertAlignment < 70 ? t('ga_admin_priority_high') : t('ga_admin_priority_medium'),
      desc: t('ga_admin_analytics_strategy_alignment_desc', {
        clinicalAlignment: formatPercent(clinicalAlignment),
        expertAlignment: formatPercent(expertAlignment),
      }),
      actions: [
        clinicalAlignment !== null && clinicalAlignment < 70
          ? t('ga_admin_analytics_strategy_alignment_action_gba_review')
          : t('ga_admin_analytics_strategy_alignment_action_gba_keep'),
        expertAlignment !== null && expertAlignment < 70
          ? t('ga_admin_analytics_strategy_alignment_action_ga_review')
          : t('ga_admin_analytics_strategy_alignment_action_ga_extract'),
        t('ga_admin_analytics_strategy_alignment_action_split'),
      ],
      prompt: t('ga_admin_analytics_strategy_alignment_prompt', {
        clinicalAlignment: formatPercent(clinicalAlignment),
        expertAlignment: formatPercent(expertAlignment),
        gbaOpenTextCount: feedbackAnalysis.clinical.openTextCount,
        gaOpenTextCount: feedbackAnalysis.expert.openTextCount,
      }),
    },
  ];
}

function buildReportPrompts(teamStats, feedbackAnalysis) {
  const clinicalGap = Math.max(teamStats.gba.targetCount - teamStats.gba.coveredCount, 0);
  const expertGap = Math.max(teamStats.ga.targetCount - teamStats.ga.coveredCount, 0);
  const missingFeedback = Math.max(
    teamStats.gba.callCount + teamStats.ga.callCount - teamStats.gba.feedbackCallCount - teamStats.ga.feedbackCallCount,
    0,
  );
  const clinicalAlignment = percent(
    feedbackAnalysis.clinical.diseasePositive + feedbackAnalysis.clinical.burdenPositive,
    feedbackAnalysis.clinical.diseaseTotal + feedbackAnalysis.clinical.burdenTotal,
  );
  const expertAlignment = percent(
    feedbackAnalysis.expert.communicationPositive,
    feedbackAnalysis.expert.communicationTotal,
  );

  return [
    t('ga_admin_analytics_report_prompt_weekly', {
      gbaCovered: teamStats.gba.coveredCount,
      gbaTotal: teamStats.gba.targetCount,
      gaCovered: teamStats.ga.coveredCount,
      gaTotal: teamStats.ga.targetCount,
      clinicalAlignment: formatPercent(clinicalAlignment),
      expertAlignment: formatPercent(expertAlignment),
    }),
    t('ga_admin_analytics_report_prompt_customer_review', {
      clinicalGap,
      expertGap,
      gbaFeedbackCustomerCount: teamStats.gba.feedbackCustomerCount,
      gaFeedbackCustomerCount: teamStats.ga.feedbackCustomerCount,
    }),
    t('ga_admin_analytics_report_prompt_tracking', {
      missingFeedback,
      unrecognizedCount: feedbackAnalysis.unrecognizedCount,
    }),
  ];
}

function buildQuestionStats(feedbacks, team) {
  const map = {};

  (feedbacks || []).forEach((feedback) => {
    const question = getFeedbackQuestionText(feedback) || t('ga_admin_unnamed_key_question');
    const answer = getFeedbackText(feedback);
    const stat = map[question] || {
      question,
      count: 0,
      positiveCount: 0,
      openTextCount: 0,
      answers: {},
    };

    stat.count += 1;
    if (isPositiveFeedback(feedback, team)) stat.positiveCount += 1;
    if (answer.length > 8) stat.openTextCount += 1;
    if (answer && answer.length <= 24) {
      stat.answers[answer] = (stat.answers[answer] || 0) + 1;
    }
    map[question] = stat;
  });

  return Object.keys(map)
    .map(key => map[key])
    .sort((a, b) => b.count - a.count);
}

function buildAnswerDistribution(answers) {
  return Object.keys(answers || {})
    .sort((a, b) => answers[b] - answers[a])
    .slice(0, 3)
    .map(answer => ({
      answer,
      count: answers[answer],
      text: `${answer} ${countFeedback(answers[answer])}`,
    }));
}

function buildQuestionInsights(questionStats, team) {
  return questionStats.slice(0, 4).map((stat) => {
    const positiveRate = percent(stat.positiveCount, stat.count);
    const answerDistribution = buildAnswerDistribution(stat.answers);
    const descParts = [];
    const promptDescParts = [];

    if (stat.positiveCount) {
      const positiveText = t(
        team === 'gba'
          ? 'ga_admin_analytics_insight_positive_alignment'
          : 'ga_admin_analytics_insight_positive_communication',
        {rate: formatPercent(positiveRate)},
      );
      descParts.push(positiveText);
      promptDescParts.push(positiveText);
    }
    if (answerDistribution.length) {
      promptDescParts.push(t('ga_admin_analytics_insight_main_answers', {
        answers: answerDistribution.map(item => item.text).join(isEnglishLocale() ? ', ' : '、'),
      }));
    }
    if (stat.openTextCount) {
      const openText = t('ga_admin_analytics_insight_open_text', {count: stat.openTextCount});
      descParts.push(openText);
      promptDescParts.push(openText);
    }

    return {
      title: stat.question,
      count: countFeedback(stat.count),
      desc: descParts.length ? descParts.join(isEnglishLocale() ? '; ' : '；') : t('ga_admin_analytics_insight_default_desc'),
      promptDesc: promptDescParts.length ? promptDescParts.join(isEnglishLocale() ? '; ' : '；') : t('ga_admin_analytics_insight_default_desc'),
      answerDistribution,
    };
  });
}

function buildFeedbackExamples(feedbacks, callById) {
  return (feedbacks || [])
    .map(feedback => ({
      question: getFeedbackQuestionText(feedback) || t('ga_admin_unnamed_key_question'),
      text: getFeedbackText(feedback),
      date: formatDateText(feedback.feedback_time || callById[getFeedbackCallId(feedback)]?.call_time),
    }))
    .filter(item => item.text.length > 8)
    .sort((a, b) => {
      const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
      if (!Number.isNaN(dateDiff) && dateDiff) return dateDiff;
      return b.text.length - a.text.length;
    })
    .slice(0, 3)
    .map(item => ({
      ...item,
      text: truncateText(item.text),
    }));
}

function buildPromptFeedbackLines(feedbacks, callById) {
  return (feedbacks || [])
    .map((feedback) => {
      const question = getFeedbackQuestionText(feedback) || t('ga_admin_unnamed_key_question');
      const answer = truncateText(getFeedbackText(feedback), 120);
      const date = formatDateText(feedback.feedback_time || callById[getFeedbackCallId(feedback)]?.call_time);
      return answer ? `${date || t('ga_admin_unknown_date')}｜${question}｜${answer}` : '';
    })
    .filter(Boolean)
    .slice(0, MAX_FEEDBACK_PROMPT_ITEMS);
}

function buildFeedbackAnalysisPrompt({typeName, teamName, metrics, insights, feedbackLines, totalCount}) {
  return [
    t('ga_admin_analytics_feedback_prompt_intro', {typeName}),
    t('ga_admin_analytics_feedback_prompt_context', {
      typeName,
      teamName,
      totalCount,
    }),
    t('ga_admin_analytics_feedback_prompt_metrics', {
      metrics: metrics.map(item => `${item.label}${item.value}`).join(isEnglishLocale() ? ', ' : '，'),
    }),
    t('ga_admin_analytics_feedback_prompt_insights', {
      insights: insights.map(item => `${item.title}${item.count}（${item.promptDesc || item.desc}）`).join(isEnglishLocale() ? '; ' : '；'),
    }),
    t('ga_admin_analytics_feedback_prompt_details', {
      feedbackLines: feedbackLines.join('\n') || t('ga_admin_analytics_no_open_feedback'),
    }),
    t('ga_admin_analytics_feedback_prompt_output'),
  ].join('\n');
}

function buildFeedbackSummaryAnalyses({feedbackByTeam, teamStats, feedbackAnalysis, callById}) {
  const clinicalAlignment = percent(
    feedbackAnalysis.clinical.diseasePositive + feedbackAnalysis.clinical.burdenPositive,
    feedbackAnalysis.clinical.diseaseTotal + feedbackAnalysis.clinical.burdenTotal,
  );
  const clinicalDisease = percent(feedbackAnalysis.clinical.diseasePositive, feedbackAnalysis.clinical.diseaseTotal);
  const clinicalBurden = percent(feedbackAnalysis.clinical.burdenPositive, feedbackAnalysis.clinical.burdenTotal);
  const expertFamiliarity = feedbackAnalysis.expert.familiarityTotal
    ? Math.round((feedbackAnalysis.expert.familiarityScore / feedbackAnalysis.expert.familiarityTotal / 3) * 100)
    : null;
  const expertAlignment = percent(
    feedbackAnalysis.expert.communicationPositive,
    feedbackAnalysis.expert.communicationTotal,
  );
  const clinicalQuestionStats = buildQuestionStats(feedbackByTeam.gba, 'gba');
  const expertQuestionStats = buildQuestionStats(feedbackByTeam.ga, 'ga');
  const clinicalInsights = buildQuestionInsights(clinicalQuestionStats, 'gba');
  const expertInsights = buildQuestionInsights(expertQuestionStats, 'ga');
  const clinicalMetrics = [
    {label: t('ga_admin_analytics_metric_total_feedback'), value: countFeedback(feedbackByTeam.gba.length)},
    {label: t('ga_admin_analytics_metric_feedback_customers'), value: countCustomer(teamStats.gba.feedbackCustomerCount)},
    {label: t('ga_admin_analytics_metric_positive_feedback_customers'), value: countCustomer(teamStats.gba.positiveFeedbackCustomerCount)},
    {label: t('ga_admin_analytics_metric_clinical_alignment'), value: formatPercent(clinicalAlignment)},
  ];
  const expertMetrics = [
    {label: t('ga_admin_analytics_metric_total_feedback'), value: countFeedback(feedbackByTeam.ga.length)},
    {label: t('ga_admin_analytics_metric_feedback_experts'), value: countExpert(teamStats.ga.feedbackCustomerCount)},
    {label: t('ga_admin_analytics_metric_expert_familiarity'), value: formatPercent(expertFamiliarity)},
    {label: t('ga_admin_analytics_metric_communication_alignment'), value: formatPercent(expertAlignment)},
  ];
  const clinicalSummary = t('ga_admin_analytics_feedback_clinical_summary', {
    feedbackCount: feedbackByTeam.gba.length,
    feedbackCustomerCount: teamStats.gba.feedbackCustomerCount,
    clinicalAlignment: formatPercent(clinicalAlignment),
    clinicalDisease: formatPercent(clinicalDisease),
    clinicalBurden: formatPercent(clinicalBurden),
    openTextCount: feedbackAnalysis.clinical.openTextCount,
  });
  const expertSummary = t('ga_admin_analytics_feedback_expert_summary', {
    feedbackCount: feedbackByTeam.ga.length,
    feedbackCustomerCount: teamStats.ga.feedbackCustomerCount,
    expertFamiliarity: formatPercent(expertFamiliarity),
    expertAlignment: formatPercent(expertAlignment),
    openTextCount: feedbackAnalysis.expert.openTextCount,
  });

  return [
    {
      key: 'clinical',
      title: t('ga_admin_analytics_feedback_clinical_title'),
      subtitle: t('ga_admin_team_gba'),
      summary: clinicalSummary,
      metrics: clinicalMetrics,
      insights: clinicalInsights,
      tags: getTopLabels(feedbackAnalysis.labels.clinical),
      examples: buildFeedbackExamples(feedbackByTeam.gba, callById),
      prompt: buildFeedbackAnalysisPrompt({
        typeName: t('ga_admin_analytics_type_clinical'),
        teamName: t('ga_admin_team_gba'),
        metrics: clinicalMetrics,
        insights: clinicalInsights,
        feedbackLines: buildPromptFeedbackLines(feedbackByTeam.gba, callById),
        totalCount: feedbackByTeam.gba.length,
      }),
    },
    {
      key: 'expert',
      title: t('ga_admin_analytics_feedback_expert_title'),
      subtitle: t('ga_admin_team_ga'),
      summary: expertSummary,
      metrics: expertMetrics,
      insights: expertInsights,
      tags: getTopLabels(feedbackAnalysis.labels.expert),
      examples: buildFeedbackExamples(feedbackByTeam.ga, callById),
      prompt: buildFeedbackAnalysisPrompt({
        typeName: t('ga_admin_customers_expert_title'),
        teamName: t('ga_admin_team_ga'),
        metrics: expertMetrics,
        insights: expertInsights,
        feedbackLines: buildPromptFeedbackLines(feedbackByTeam.ga, callById),
        totalCount: feedbackByTeam.ga.length,
      }),
    },
  ];
}

function notifyProgress(onProgress, payload) {
  if (typeof onProgress === 'function') {
    onProgress(payload);
  }
}

export async function loadGaAdminAnalytics({onProgress} = {}) {
  const userId = getLocalStorage('user_id');
  const roleId = getLocalStorage('role_id');
  const tas = getLocalStorage('tas');

  notifyProgress(onProgress, {completedModules: 0, labelKey: 'ga_admin_loading_overview_source', totalModules: 4});
  const {clinicalRaw, expertRaw, calls, feedbacks: allFeedbacks} = await loadSourceData({userId, roleId, tas});

  notifyProgress(onProgress, {completedModules: 1, labelKey: 'ga_admin_loading_overview_customers', totalModules: 4});
  const expertCustomers = uniqBy(expertRaw, item => toId(item.physician_id));
  const expertIds = new Set(expertCustomers.map(item => toId(item.physician_id)));
  let clinicalCustomers = uniqBy(clinicalRaw, item => toId(item.physician_id))
    .filter(item => !expertIds.has(toId(item.physician_id)) && !isExpertCustomer(item));
  if (!clinicalCustomers.length && clinicalRaw.length) {
    clinicalCustomers = uniqBy(clinicalRaw, item => toId(item.physician_id));
  }

  const clinicalIds = new Set(clinicalCustomers.map(item => toId(item.physician_id)));
  const classifiedCalls = classifyCalls(calls, clinicalIds, expertIds);
  const callTeamById = {};
  const callById = {};
  classifiedCalls.gba.forEach((call) => { callById[getCallId(call)] = call; });
  classifiedCalls.ga.forEach((call) => { callById[getCallId(call)] = call; });
  classifiedCalls.gba.forEach((call) => { callTeamById[getCallId(call)] = 'gba'; });
  classifiedCalls.ga.forEach((call) => { callTeamById[getCallId(call)] = 'ga'; });

  const relevantCallIds = Object.keys(callTeamById);
  const relevantCallIdSet = new Set(relevantCallIds);
  const feedbacks = relevantCallIds.length
    ? allFeedbacks.filter(item => relevantCallIdSet.has(getFeedbackCallId(item)))
    : [];

  notifyProgress(onProgress, {completedModules: 2, labelKey: 'ga_admin_loading_overview_feedback', totalModules: 4});
  const feedbackByTeam = {
    gba: feedbacks.filter(item => callTeamById[getFeedbackCallId(item)] === 'gba'),
    ga: feedbacks.filter(item => callTeamById[getFeedbackCallId(item)] === 'ga'),
  };
  const feedbackAnalysis = analyzeFeedbacks(feedbacks, callTeamById);

  notifyProgress(onProgress, {completedModules: 3, labelKey: 'ga_admin_loading_overview_dashboard', totalModules: 4});
  const teamStats = {
    gba: {
      ...buildTeamStats(clinicalCustomers, classifiedCalls.gba, feedbackByTeam.gba),
      ...buildFeedbackCustomerStats(feedbackByTeam.gba, 'gba', callById),
    },
    ga: {
      ...buildTeamStats(expertCustomers, classifiedCalls.ga, feedbackByTeam.ga),
      ...buildFeedbackCustomerStats(feedbackByTeam.ga, 'ga', callById),
    },
  };
  const latestCallDate = getLatestDateFromItems(
    [...classifiedCalls.gba, ...classifiedCalls.ga],
    item => item.call_time,
  );
  const latestFeedbackDate = getLatestDateFromItems(feedbacks, item => item.feedback_time);
  const monthKeys = getMonthKeysFromProjectStart(latestCallDate > latestFeedbackDate ? latestCallDate : latestFeedbackDate);
  return {
    overviewStats: buildOverviewStats(teamStats, feedbackAnalysis),
    aiHighlights: buildHighlights(teamStats, feedbackAnalysis),
    mindsetBoards: buildMindsetBoards(feedbackAnalysis),
    customerPanoramas: buildCustomerPanoramas(teamStats, feedbackAnalysis),
    executionCards: buildExecutionCards(teamStats),
    executionPipelines: buildExecutionPipelines(teamStats),
    positiveFeedbackRules: buildPositiveFeedbackRules(),
    monthlyVisitChart: buildMonthlyVisitChart(classifiedCalls, monthKeys),
    monthlyFeedbackChart: buildMonthlyFeedbackChart(feedbacks, callTeamById, callById, monthKeys),
    collaborationQueue: buildCollaborationQueue(teamStats, feedbackAnalysis),
    qualityAlerts: buildQualityAlertsWithDetails({
      teamStats,
      feedbackAnalysis,
      clinicalCustomers,
      expertCustomers,
      classifiedCalls,
      feedbacks,
      callById,
      callTeamById,
    }),
    feedbackSummaryAnalyses: buildFeedbackSummaryAnalyses({feedbackByTeam, teamStats, feedbackAnalysis, callById}),
    strategyRecommendations: buildStrategyRecommendations(teamStats, feedbackAnalysis),
    reportPrompts: buildReportPrompts(teamStats, feedbackAnalysis),
    raw: {
      clinicalCustomers,
      expertCustomers,
      calls,
      feedbacks,
    },
  };
}
